import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ChevronDownIcon,
  ExpandLess as ChevronUpIcon,
  SmartToy as SmartToyIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  EmojiEvents as TrophyIcon,
  ChatBubbleOutline as CommentIcon,
  Article as PostIcon,
} from "@mui/icons-material";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { DeviceContext } from "../context/DeviceContext";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../utils/imageUrl";
import PositionsTable from "./PositionsTable";
import PostHistoryTable from "./PostHistoryTable";
import CommentsHistoryTable from "./CommentsHistoryTable";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#cbd4e5",
  textMuted: "#e7f6e4",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

// ── Collapsible section (same as CommunityPage) ───────────────────────────────
function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!children) return null;
  return (
    <Box
      sx={{
        background: DL.bgMedium,
        border: `1px solid #808080`,
        borderRadius: "12px",
        overflow: "hidden",
        mb: 1.5,
      }}>
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1.5,
          cursor: "pointer",
          "&:hover": { background: DL.bgLight },
          transition: "background 0.2s ease",
        }}>
        <Typography
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: "0.875rem",
            color: DL.green,
          }}>
          {title}
        </Typography>
        {open ? (
          <ChevronUpIcon sx={{ fontSize: 18, color: DL.textMuted }} />
        ) : (
          <ChevronDownIcon sx={{ fontSize: 18, color: DL.textMuted }} />
        )}
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: DL.textPrimary,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}>
            {children}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Stat item ─────────────────────────────────────────────────────────────────
function StatItem({ icon: Icon, value, label }) {
  return (
    <Box sx={{ textAlign: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}>
        {Icon && <Icon sx={{ fontSize: 14, color: DL.green }} />}
        <Typography
          sx={{ fontWeight: 700, fontSize: "1.1rem", color: DL.green }}>
          {value ?? 0}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: "10px",
          color: DL.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}>
        {label}
      </Typography>
    </Box>
  );
}

const SIDEBAR_RIGHT_WIDTH = 300;
const TOPBAR_HEIGHT = 64;

const ProfileComponent = ({ username }) => {
  const { isMobile } = useContext(DeviceContext);
  const { user, isLoggedIn } = useContext(AuthContext);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [mobileTab, setMobileTab] = useState("posts");
  const navigate = useNavigate();
  const [bannerUrl, setBannerUrl] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  const isOwnProfile = isLoggedIn && user?.username === username;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/userinfo/getInfo`,
          { params: { username } },
        );
        setUserDetails(response.data);
        setBannerUrl(
          response.data.bannerUrl ? getImageUrl(response.data.bannerUrl) : null,
        );
        setAvatarUrl(
          response.data.avatarUrl ? getImageUrl(response.data.avatarUrl) : null,
        );
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [username]);

  useEffect(() => {
    const checkFollowing = async () => {
      if (isLoggedIn && userDetails && !isOwnProfile) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SC_API_BASE_URL}/api/userfollow/isFollowing/${username}`,
            { headers: { Authorization: `Bearer ${user.token}` } },
          );
          setIsFollowing(response.data.message === "true");
        } catch (error) {
          console.error("Error checking following status:", error);
        }
      }
    };
    checkFollowing();
  }, [isLoggedIn, userDetails, user, username, isOwnProfile]);

  const handleFollow = async () => {
    if (!isLoggedIn) {
      navigate("/signin");
      return;
    }
    try {
      const url = isFollowing
        ? `${process.env.REACT_APP_SC_API_BASE_URL}/api/userfollow/unfollow/${username}`
        : `${process.env.REACT_APP_SC_API_BASE_URL}/api/userfollow/follow/${username}`;
      await axios.post(
        url,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } },
      );
      setIsFollowing(!isFollowing);
      setUserDetails((prev) => ({
        ...prev,
        followersCount: prev.followersCount + (isFollowing ? -1 : 1),
      }));
    } catch (error) {
      console.error("Error following/unfollowing:", error);
    }
  };

  // ── Profile header (banner + avatar + name + stats + actions) ──────────────
  const ProfileHeader = () => (
    <Box>
      {/* Banner */}
      <Box
        sx={{
          height: isMobile ? 120 : 160,
          background: bannerUrl
            ? `url(${bannerUrl}) center/cover no-repeat`
            : `linear-gradient(135deg, rgba(16,56,32,0.5) 0%, rgba(6,30,20,0.5) 100%)`,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, #0f1419 0%, transparent 60%)",
          },
        }}
      />

      {/* Avatar + info row */}
      <Box sx={{ px: isMobile ? 2 : 3, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mt: isMobile ? "-36px" : "-48px",
            mb: 1.5,
            position: "relative",
            zIndex: 1,
          }}>
          {/* Left: avatar + username side by side (desktop only) */}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
            <Avatar
              alt={username}
              src={avatarUrl || undefined}
              sx={{
                width: isMobile ? 72 : 96,
                height: isMobile ? 72 : 96,
                border: `4px solid ${DL.bgDark}`,
                boxShadow: "0px 4px 12px rgba(98,244,129,0.3)",
                fontSize: isMobile ? "1.5rem" : "2rem",
                fontWeight: 700,
                bgcolor: `hsl(${(username?.charCodeAt(0) * 20) % 360}, 40%, 35%)`,
                flexShrink: 0,
              }}>
              {username?.charAt(0).toUpperCase()}
            </Avatar>

            {/* Username + bot chip inline with avatar (desktop only) */}
            {!isMobile && (
              <Box sx={{ mt: "52px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.3rem",
                      color: DL.green,
                    }}>
                    {username}
                  </Typography>
                  {userDetails?.bot && (
                    <Chip
                      icon={
                        <SmartToyIcon sx={{ fontSize: "12px !important" }} />
                      }
                      label="Bot"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(249,249,207,0.08)",
                        color: DL.textSecondary,
                        border: `1px solid ${DL.borderSubtle}`,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        height: 22,
                        "& .MuiChip-icon": { color: DL.textSecondary },
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                  <StatItem
                    icon={TrophyIcon}
                    value={userDetails?.postTendies}
                    label="Post Tendies"
                  />
                  <StatItem
                    icon={CommentIcon}
                    value={userDetails?.commentTendies}
                    label="Comment Tendies"
                  />
                  <StatItem
                    value={userDetails?.followersCount}
                    label="Followers"
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {isOwnProfile ? (
              <>
                <Button
                  onClick={() => navigate("/edit-photos")}
                  size="small"
                  sx={{
                    backgroundColor: "transparent",
                    border: `1px solid ${DL.borderSubtle}`,
                    color: DL.textSecondary,
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: DL.borderGreen,
                      color: DL.textPrimary,
                    },
                  }}>
                  Edit Photos
                </Button>
                <Button
                  onClick={() => navigate("/add-user-profile")}
                  startIcon={<EditIcon sx={{ fontSize: "14px !important" }} />}
                  size="small"
                  sx={{
                    background: `linear-gradient(to right, #10b981, #059669)`,
                    color: "#fff",
                    borderRadius: "8px",
                    px: 1.5,
                    py: 0.75,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    textTransform: "none",
                    boxShadow: "0 2px 8px rgba(98,244,129,0.3)",
                    "&:hover": { filter: "brightness(110%)" },
                  }}>
                  Edit Profile
                </Button>
              </>
            ) : (
              <Button
                onClick={handleFollow}
                startIcon={
                  isFollowing ? (
                    <PersonRemoveIcon sx={{ fontSize: "14px !important" }} />
                  ) : (
                    <PersonAddIcon sx={{ fontSize: "14px !important" }} />
                  )
                }
                size="small"
                sx={{
                  background: isFollowing
                    ? "transparent"
                    : `linear-gradient(to right, #10b981, #059669)`,
                  border: isFollowing ? `1px solid ${DL.borderSubtle}` : "none",
                  color: isFollowing ? DL.textSecondary : "#fff",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  textTransform: "none",
                  boxShadow: isFollowing
                    ? "none"
                    : "0 2px 8px rgba(98,244,129,0.3)",
                  "&:hover": {
                    filter: "brightness(110%)",
                    borderColor: isFollowing ? DL.borderGreen : "none",
                  },
                }}>
                {isFollowing ? "Following" : "Follow"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Mobile only: name + stats below avatar */}
        {isMobile && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: DL.textPrimary,
                }}>
                {username}
              </Typography>
              {userDetails?.bot && (
                <Chip
                  icon={<SmartToyIcon sx={{ fontSize: "12px !important" }} />}
                  label="Bot"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(249,249,207,0.08)",
                    color: DL.textSecondary,
                    border: `1px solid ${DL.borderSubtle}`,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    height: 22,
                    "& .MuiChip-icon": { color: DL.textSecondary },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 3 }}>
              <StatItem
                icon={TrophyIcon}
                value={userDetails?.postTendies}
                label="Post Tendies"
              />
              <StatItem
                icon={CommentIcon}
                value={userDetails?.commentTendies}
                label="Comment Tendies"
              />
              <StatItem value={userDetails?.followersCount} label="Followers" />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  // ── Info panel (right sidebar content) ────────────────────────────────────
  const InfoPanel = () => (
    <Box sx={{ pt: 0, pb: 2, px: 0 }}>
      {userDetails?.aboutMe ||
      userDetails?.investmentPhilosophy ||
      userDetails?.investmentStrategies ||
      userDetails?.disclaimers ? (
        <>
          {userDetails.aboutMe && (
            <CollapsibleSection title="About Me" defaultOpen>
              {userDetails.aboutMe}
            </CollapsibleSection>
          )}
          {userDetails.investmentPhilosophy && (
            <CollapsibleSection title="Investment Philosophy">
              {userDetails.investmentPhilosophy}
            </CollapsibleSection>
          )}
          {userDetails.investmentStrategies && (
            <CollapsibleSection title="Investment Strategies">
              {userDetails.investmentStrategies}
            </CollapsibleSection>
          )}
          {userDetails.disclaimers && (
            <CollapsibleSection title="Disclaimer">
              {userDetails.disclaimers}
            </CollapsibleSection>
          )}
        </>
      ) : (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: DL.textMuted,
              fontStyle: "italic",
            }}>
            No profile info yet.
          </Typography>
        </Box>
      )}
    </Box>
  );

  // ── Activity tabs ──────────────────────────────────────────────────────────
  const ActivityTabs = () => (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{
          borderBottom: `1px solid ${DL.border}`,
          "& .MuiTab-root": {
            color: DL.textMuted,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.875rem",
            minHeight: 44,
          },
          "& .Mui-selected": { color: DL.green },
          "& .MuiTabs-indicator": { backgroundColor: DL.green },
        }}>
        <Tab label="Posts" />
        <Tab label="Comments" />
        <Tab label="Positions" />
      </Tabs>
      <Box sx={{ pt: 2 }}>
        {activeTab === 0 && <PostHistoryTable userId={userDetails?.userId} />}
        {activeTab === 1 && (
          <CommentsHistoryTable userId={userDetails?.userId} />
        )}
        {activeTab === 2 && <PositionsTable userId={userDetails?.userId} />}
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: DL.bgDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <CircularProgress sx={{ color: DL.green }} />
      </Box>
    );
  }

  if (!userDetails) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: DL.bgDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Typography sx={{ color: DL.textSecondary }}>
          User not found.
        </Typography>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        pt: `${TOPBAR_HEIGHT}px`,
      }}>
      {isMobile ? (
        // ══════════════════════════════════════════════════════════════════
        // MOBILE
        // ══════════════════════════════════════════════════════════════════
        <Box>
          <ProfileHeader />

          {/* Mobile tab bar */}
          <Box
            sx={{
              display: "flex",
              borderBottom: `1px solid ${DL.border}`,
              borderTop: `1px solid ${DL.border}`,
              backgroundColor: DL.bgMedium,
            }}>
            {["posts", "comments", "positions", "about"].map((tab) => (
              <Box
                key={tab}
                onClick={() => setMobileTab(tab)}
                sx={{
                  flex: 1,
                  py: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "capitalize",
                  color: mobileTab === tab ? DL.green : DL.textMuted,
                  borderBottom:
                    mobileTab === tab
                      ? `2px solid ${DL.green}`
                      : "2px solid transparent",
                  transition: "all 0.2s ease",
                }}>
                {tab}
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 2, py: 2 }}>
            {mobileTab === "posts" && (
              <PostHistoryTable userId={userDetails?.userId} />
            )}
            {mobileTab === "comments" && (
              <CommentsHistoryTable userId={userDetails?.userId} />
            )}
            {mobileTab === "positions" && (
              <PositionsTable userId={userDetails?.userId} />
            )}
            {mobileTab === "about" && <InfoPanel />}
          </Box>
        </Box>
      ) : (
        // ══════════════════════════════════════════════════════════════════
        // DESKTOP — profile header full width, then 2-column below
        // ══════════════════════════════════════════════════════════════════
        <Box>
          {/* Profile header */}
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <ProfileHeader />
          </Box>

          {/* Two-column content */}
          <Box
            sx={{
              maxWidth: 1200,
              mx: "auto",
              display: "flex",
              gap: 0,
              alignItems: "flex-start",
              px: 3,
              py: 3,
            }}>
            {/* Center: activity */}
            <Box sx={{ flex: 1, minWidth: 0, mr: 3 }}>
              <Box
                sx={{
                  backgroundColor: DL.bgMedium,
                  border: `1px solid ${DL.border}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}>
                <ActivityTabs />
              </Box>
            </Box>

            {/* Right: info panel */}
            <Box sx={{ width: SIDEBAR_RIGHT_WIDTH, flexShrink: 0 }}>
              <InfoPanel />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProfileComponent;
